import React, {
    createContext,
    useContext,
    useState,
    type Dispatch,
    type SetStateAction,
    type PropsWithChildren,
} from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { getHeroOffset } from '@fiverr-private/variations_display';
import { INTEGRATIONS } from '../../../constants';
import usePageScroll from '../../../lib/usePageScroll';
import getLogoMakerIntegrationFromPath from '../../../lib/getLogoMakerIntegrationFromPath';

export type LogoMakerIntegration = (typeof INTEGRATIONS)[keyof typeof INTEGRATIONS];

export interface LogoMakerContextValue {
    integration: LogoMakerIntegration;
    showBackButton: boolean;
    isTransparent: boolean;
    isSticky: boolean;
    logoIdeasMenuIsOpened: boolean;
    setLogoIdeasMenuIsOpened: Dispatch<SetStateAction<boolean>>;
}

const defaultValue: LogoMakerContextValue = {
    integration: INTEGRATIONS.NONE,
    showBackButton: false,
    isTransparent: false,
    isSticky: false,
    logoIdeasMenuIsOpened: false,
    setLogoIdeasMenuIsOpened: () => undefined,
};

const LogoMakerContext = createContext<LogoMakerContextValue>(defaultValue);

export const useLogoMakerContext = (): LogoMakerContextValue => useContext(LogoMakerContext);

export interface LogoMakerProviderProps {
    showBackButton?: boolean;
    integration?: LogoMakerIntegration;
    isTransparent?: boolean;
    isSticky?: boolean;
}

export const LogoMakerProvider = ({ children, ...props }: PropsWithChildren<LogoMakerProviderProps>) => {
    const {
        showBackButton,
        integration: integrationProp,
        isTransparent: isTransparentProp = false,
        isSticky: isStickyProp = false,
    } = props;

    const { url } = getContext();
    let pathname = '/';
    try {
        pathname = new URL(url).pathname;
    } catch {
        /* Storybook / SSR fallback */
    }
    const integration = integrationProp || getLogoMakerIntegrationFromPath(pathname) || INTEGRATIONS.NONE;

    const [logoIdeasMenuIsOpened, setLogoIdeasMenuIsOpened] = useState(false);
    const [pageIsScrolledDown, setPageIsScrolledDown] = useState(false);

    usePageScroll(() => {
        const heroOffset = getHeroOffset();
        setPageIsScrolledDown(window.scrollY > heroOffset && heroOffset > 0);
    });

    const isSticky = Boolean(isStickyProp && pageIsScrolledDown);
    const isTransparent = Boolean(isTransparentProp && !isSticky && !logoIdeasMenuIsOpened);

    const value: LogoMakerContextValue = {
        integration,
        showBackButton: Boolean(showBackButton),
        isTransparent,
        isSticky,
        logoIdeasMenuIsOpened,
        setLogoIdeasMenuIsOpened,
    };

    return <LogoMakerContext.Provider value={value}>{children}</LogoMakerContext.Provider>;
};

export default LogoMakerContext;
